/** /ai-spend — the AI spend ledger (backend metaplan A5, AIE plan §9/§10).
 *
 * Every metered LLM call is a ledger row: tenant (internal work is tenant 0), user,
 * feature, model, tokens and cost. The page answers "what does AI cost, and for whom":
 *
 * - a summary over the current filters, grouped by feature, tenant, user, day or model
 *   (`?group=`); clicking a feature / tenant / user / day row narrows the filters to it;
 * - the calls grid below, newest first or by cost.
 *
 * Filters live in the URL (linkable) and feed both: tenant (incl. "Internal"), user,
 * feature, the When window, and the URL-only correlation / task ids that link here from a
 * diagnose thread. Gated by `console_ai_spend_view` (Ops Admin only by default). */
import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getGridSingleSelectOperators, type GridColDef } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

import * as aiSpendApi from '@/api/aiSpend';
import {
  AI_SPEND_GROUPS,
  formatUsd,
  type AISpendEntry,
  type AISpendGroupBy,
  type AISpendSummaryRow,
} from '@/api/aiSpend';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import {
  TenantPicker,
  pickedSentinel,
  type TenantPickerValue,
} from '@/components/TenantPicker/TenantPicker';
import { boundToParam, paramToBound, TimeWindowPicker } from '@/components/TimeWindowPicker';
import { UserPicker } from '@/components/UserPicker/UserPicker';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApiWith } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';
import { formatDate, formatRelativeTime } from '@/utils/format';

/** Tenant 0 in the ledger: work no tenant asked for (ops diagnoses of internal users…). */
export const INTERNAL_TENANT: TenantPickerValue = { id: 0, name: 'Internal (no tenant)' };
const INTERNAL_OPTIONS = [INTERNAL_TENANT];

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);
/** An ISO instant or a relative token (`now-7d`) — the backend reads both. */
const isoOrDrop = (v: string) => (paramToBound(v) ? v : undefined);

/** URL `tenant` → `tour_operator`: operator ids (`12,845`), or Internal's `0`
 *  alone (sentinels are exclusive, D3). */
const tenantToApi = tenantIdsToApiWith(['0']);

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator', toApi: tenantToApi, tenantScope: 'multi' },
  { param: 'user', toApi: digits },
  { param: 'feature', column: { field: 'feature', operator: 'is' } },
  { param: 'from', apiParam: 'created_after', toApi: isoOrDrop },
  { param: 'to', apiParam: 'created_before', toApi: isoOrDrop },
  // URL-only: deep links from a diagnose thread or a background task.
  { param: 'correlation_id' },
  { param: 'bgtask_id' },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

/** Column order (mirrors the columns memo — keep in sync). */
const FIELD_ORDER = [
  'created_ts',
  'tenant',
  'user',
  'feature',
  'stage',
  'model',
  'input_tokens',
  'output_tokens',
  'cache_creation_tokens',
  'cache_read_tokens',
  'cost_usd',
  'correlation_id',
  'bgtask_id',
];
const DEFAULT_HIDDEN = ['stage', 'cache_creation_tokens', 'cache_read_tokens', 'correlation_id'];

const isOperator = getGridSingleSelectOperators().filter((op) => op.value === 'is');
const tokens = new Intl.NumberFormat('en-US');
const dayStart = (key: string) => new Date(`${key}T00:00:00Z`);

const isGroup = (v: string | null): v is AISpendGroupBy =>
  AI_SPEND_GROUPS.some((g) => g.value === v);

export function AISpendPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const groupBy: AISpendGroupBy = isGroup(groupParam) ? groupParam : 'feature';
  const setGroupBy = (value: AISpendGroupBy) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'feature') next.delete('group');
        else next.set('group', value);
        return next;
      },
      { replace: true },
    );

  const presets = useGridPresets({
    grid: 'ai_spend',
    defaultOrder: FIELD_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });
  const {
    filterModel,
    onFilterModelChange,
    extra,
    getParam,
    setParam,
    setParams,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  const summaryQuery = useQuery({
    queryKey: ['aiSpendSummary', groupBy, extra],
    queryFn: () => aiSpendApi.summary(groupBy, extra as aiSpendApi.AISpendFilters),
    // Switching the grouping or a filter keeps the last summary on screen until the new one lands.
    placeholderData: keepPreviousData,
  });
  const features = useMemo(() => summaryQuery.data?.features ?? [], [summaryQuery.data]);

  const tenantRaw = getParam('tenant');
  // The picker's value: the operators the effective filter names (URL > preset >
  // scope), or exactly the Internal sentinel.
  const scopeList = useTenantScopeList();
  const tenantValues: TenantPickerValue[] =
    tenantRaw?.trim() === '0' ? [INTERNAL_TENANT] : pageTenantsFrom(tenantRaw, scopeList);
  const userRaw = getParam('user');
  const userId = userRaw && /^\d+$/.test(userRaw) ? Number(userRaw) : null;
  const from = getParam('from');
  const to = getParam('to');

  /** Narrow the filters to a summary row (the row's group decides which filter).
   *  A tenant row replaces the tenant filter with that one tenant (or Internal). */
  const drillDown = useCallback(
    (row: AISpendSummaryRow) => {
      if (groupBy === 'feature') setParam('feature', row.key);
      else if (groupBy === 'tenant') setParam('tenant', row.key);
      else if (groupBy === 'user' && row.key) setParam('user', row.key);
      else if (groupBy === 'day') {
        const start = dayStart(row.key);
        // The day's last second: a To bound covers through the end of it.
        const end = new Date(start.getTime() + 24 * 3_600_000 - 1000);
        setParams({ from: boundToParam(start), to: boundToParam(end) });
      }
    },
    [groupBy, setParam, setParams],
  );
  const canDrill = groupBy !== 'model';

  const columns = useMemo<GridColDef<AISpendEntry>[]>(
    () => [
      {
        field: 'created_ts',
        headerName: 'When',
        width: 170,
        filterable: false,
        renderCell: (p) => (
          <span title={formatDate(p.row.created_ts)}>{formatRelativeTime(p.row.created_ts)}</span>
        ),
      },
      {
        field: 'tenant',
        headerName: 'Tenant',
        width: 170,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.tenant?.code ?? (row.tenant_id === 0 ? 'Internal' : `#${row.tenant_id}`),
        renderCell: (p) =>
          p.row.tenant ? (
            <WithTenantMark environment={p.row.tenant.environment}>
              <RouterLink to={`/tour-operators/${p.row.tenant.id}`} onClick={(e) => e.stopPropagation()}>
                {p.row.tenant.code || p.row.tenant.name}
              </RouterLink>
            </WithTenantMark>
          ) : p.row.tenant_id === 0 ? (
            <Typography variant="body2" color="text.secondary" component="span">
              Internal
            </Typography>
          ) : (
            `#${p.row.tenant_id}`
          ),
      },
      {
        field: 'user',
        headerName: 'User',
        flex: 1,
        minWidth: 180,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.user?.email ?? '',
        renderCell: (p) =>
          p.row.user ? (
            <RouterLink to={`/users/${p.row.user.id}`} onClick={(e) => e.stopPropagation()}>
              {p.row.user.email || `#${p.row.user.id}`}
            </RouterLink>
          ) : (
            '—'
          ),
      },
      {
        field: 'feature',
        headerName: 'Feature',
        width: 230,
        sortable: false,
        type: 'singleSelect',
        valueOptions: features.map((f) => ({ value: f.code, label: f.label })),
        filterOperators: isOperator,
        renderCell: (p) => p.row.feature_label,
      },
      { field: 'stage', headerName: 'Stage', width: 170, sortable: false, filterable: false },
      {
        field: 'model',
        headerName: 'Model',
        width: 150,
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => row.model_label,
        renderCell: (p) => <span title={p.row.model_id}>{p.row.model_label}</span>,
      },
      ...(
        [
          ['input_tokens', 'Input tokens'],
          ['output_tokens', 'Output tokens'],
          ['cache_creation_tokens', 'Cache writes'],
          ['cache_read_tokens', 'Cache reads'],
        ] as const
      ).map(
        ([field, headerName]): GridColDef<AISpendEntry> => ({
          field,
          headerName,
          width: 120,
          type: 'number',
          sortable: false,
          filterable: false,
          valueFormatter: (value: number) => tokens.format(value),
        }),
      ),
      {
        field: 'cost_usd',
        headerName: 'Cost',
        width: 110,
        type: 'number',
        filterable: false,
        valueFormatter: (value: number) => formatUsd(value),
      },
      {
        field: 'correlation_id',
        headerName: 'Correlation',
        width: 290,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.correlation_id ? (
            <RouterLink
              to={`/ai-spend?correlation_id=${encodeURIComponent(p.row.correlation_id)}`}
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.correlation_id}
            </RouterLink>
          ) : (
            '—'
          ),
      },
      {
        field: 'bgtask_id',
        headerName: 'Task',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p.row.bgtask_id ? (
            <RouterLink to={`/bgtasks/${p.row.bgtask_id}`} onClick={(e) => e.stopPropagation()}>
              Open task
            </RouterLink>
          ) : (
            '—'
          ),
      },
    ],
    [features],
  );

  const queryFn = (params: ResourceDataGridParams) =>
    aiSpendApi.list({
      ...(params.extra as aiSpendApi.AISpendFilters),
      ordering: params.ordering ?? presets.ordering,
      limit: params.pageSize,
      offset: (params.page - 1) * params.pageSize,
      ...(params.timeoutTier ? { timeout: params.timeoutTier } : {}),
    });

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'when',
      priority: 6,
      active: Boolean(from || to),
      node: (
        <TimeWindowPicker
          label="When"
          allowOpenEnded
          value={[paramToBound(from), paramToBound(to, { end: true })]}
          onChange={([start, end]) => setParams({ from: boundToParam(start), to: boundToParam(end) })}
        />
      ),
    },
    {
      key: 'tenant',
      priority: 5,
      active: tenantValues.length > 0,
      node: (
        <Box sx={{ minWidth: 260 }}>
          <TenantPicker
            multiple
            value={tenantValues}
            onChange={(next) =>
              setParam(
                'tenant',
                pickedSentinel(next, INTERNAL_OPTIONS)
                  ? String(INTERNAL_TENANT.id)
                  : formatTenantIds(next.map((t) => t.id)),
              )
            }
            extraOptions={INTERNAL_OPTIONS}
          />
        </Box>
      ),
    },
    {
      key: 'feature',
      priority: 4,
      active: Boolean(getParam('feature')),
      node: (
        <TextField
          select
          size="small"
          label="Feature"
          value={getParam('feature') ?? ''}
          onChange={(e) => setParam('feature', e.target.value || null)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All features</MenuItem>
          {features.map((f) => (
            <MenuItem key={f.code} value={f.code}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'user',
      priority: 3,
      active: userId !== null,
      node: (
        <Box sx={{ minWidth: 240 }}>
          <UserPicker
            value={userId !== null ? { id: userId, email: `user #${userId}` } : null}
            onChange={(u) => setParam('user', u ? String(u.id) : null)}
          />
        </Box>
      ),
    },
  ];

  const correlationId = getParam('correlation_id');
  const bgtaskId = getParam('bgtask_id');

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="ai-spend"
        title="AI spend"
        subtitle="Every metered LLM call: what AI costs, by tenant, user, feature and day."
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {(correlationId || bgtaskId) && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setParams({ correlation_id: null, bgtask_id: null })}
        >
          {correlationId ? `Calls of one unit of work: ${correlationId}` : `Calls of task ${bgtaskId}`}
        </Alert>
      )}

      <SpendSummary
        groupBy={groupBy}
        onGroupBy={setGroupBy}
        query={summaryQuery}
        onRow={canDrill ? drillDown : undefined}
      />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<AISpendEntry>
          queryKey={['aiSpend']}
          queryFn={queryFn}
          allowExtendedTimeout
          columns={orderColumns(columns, presets.orderedFields)}
          defaultPageSize={50}
          defaultSort={{ field: 'created_ts', sort: 'desc' }}
          extra={extra}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            exportFileName: 'ai-spend',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}

interface SummaryProps {
  groupBy: AISpendGroupBy;
  onGroupBy: (value: AISpendGroupBy) => void;
  query: UseQueryResult<aiSpendApi.AISpendSummary>;
  /** Narrow the page's filters to a row; absent for groups with no filter (model). */
  onRow?: (row: AISpendSummaryRow) => void;
}

/** Totals over the filters + one row per group, with each row's share of the cost. */
function SpendSummary({ groupBy, onGroupBy, query, onRow }: SummaryProps) {
  const data = query.data;
  const total = data?.totals.cost_usd ?? 0;
  return (
    <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1, flexWrap: 'wrap' }} useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={groupBy}
          onChange={(_, value: AISpendGroupBy | null) => value && onGroupBy(value)}
          aria-label="Group spend by"
        >
          {AI_SPEND_GROUPS.map((g) => (
            <ToggleButton key={g.value} value={g.value}>
              {g.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {data && (
          <Typography variant="body2" color="text.secondary" data-testid="ai-spend-totals">
            <strong>{formatUsd(data.totals.cost_usd)}</strong> over {tokens.format(data.totals.calls)} calls ·{' '}
            {tokens.format(data.totals.input_tokens)} input / {tokens.format(data.totals.output_tokens)} output
            tokens
          </Typography>
        )}
      </Stack>
      {query.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : query.isError ? (
        <Alert severity="error">Couldn’t load the spend summary.</Alert>
      ) : data && data.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No metered AI calls match these filters.
        </Typography>
      ) : (
        <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
          <Table size="small" stickyHeader aria-label="AI spend summary">
            <TableHead>
              <TableRow>
                <TableCell>{AI_SPEND_GROUPS.find((g) => g.value === groupBy)?.label}</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Input tokens</TableCell>
                <TableCell align="right">Output tokens</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell sx={{ width: 160 }}>Share</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.rows.map((row) => {
                const share = total > 0 ? row.cost_usd / total : 0;
                const clickable = onRow && !(groupBy === 'user' && !row.key);
                return (
                  <TableRow
                    key={row.key}
                    hover={Boolean(clickable)}
                    onClick={clickable ? () => onRow(row) : undefined}
                    sx={clickable ? { cursor: 'pointer' } : undefined}
                    title={clickable ? 'Show only these calls' : undefined}
                  >
                    <TableCell>{row.label || row.key || '—'}</TableCell>
                    <TableCell align="right">{tokens.format(row.calls)}</TableCell>
                    <TableCell align="right">{tokens.format(row.input_tokens)}</TableCell>
                    <TableCell align="right">{tokens.format(row.output_tokens)}</TableCell>
                    <TableCell align="right">{formatUsd(row.cost_usd)}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}
                        >
                          <Box sx={{ width: `${share * 100}%`, height: 1, bgcolor: 'primary.main' }} />
                        </Box>
                        <Typography variant="caption" sx={{ width: 36, textAlign: 'right' }}>
                          {Math.round(share * 100)}%
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {data?.truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
              Showing the 500 largest groups — narrow the filters for the rest.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}
